import React, {useState, useRef} from 'react';
import {Loader} from 'lucide-react';
import {useNavigate} from "react-router-dom";
import {Controller, useForm} from "react-hook-form";
import {useMutation, useQuery} from '@tanstack/react-query';
import secureStorage from "../utils/secureStorage";
import {api} from "../config/api";
import FormListbox from "../components/Common/FormListbox";
import ClientTypeEditor from "../components/Setting/ClientTypeEditor";
import {createLoyaltyCard, fetchLoyaltyProgram} from "../components/Loyalty/loyaltyApi";
import {FormHeader, Field, Section, ClientPhoto, inputClass, initialsOf, COUNTRY_CODES, CIVILITES} from "./clientFormUI";
import Notification from "../components/Common/Notification";
import DateInput from "../components/Common/DateInput";

const ClientNewForm = () => {
    const navigate = useNavigate();
    const [clientImage, setClientImage] = useState(null);
    const [showClientTypeEditor, setShowClientTypeEditor] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        type: '',
        message: ''
    });

    const {
        register,
        control,
        handleSubmit,
        formState: {errors},
        reset,
        watch,
        setValue
    } = useForm();
    const formRef = useRef(null);
    const fileInputRef = useRef(null);

    // Client types (VIP, VVIP, ...) are configured per account in Paramètres > Types de clients
    const {data: clientTypes = []} = useQuery({
        queryKey: ['client-types', 'all'],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/client-types?itemsPerPage=100', {
                headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
            });
            return response.data.items || [];
        }
    });

    // Handle image upload
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showNotification('error', 'Veuillez sélectionner un fichier image valide');
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                showNotification('error', 'La taille de l\'image ne doit pas dépasser 5MB');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => setClientImage(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    // Remove selected image
    const removeImage = () => {
        setClientImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Trigger file input click
    const triggerImageUpload = () => {
        fileInputRef.current?.click();
    };

    const showNotification = (type, message) => {
        setNotification({
            show: true,
            type,
            message
        });

        if (type === 'success') {
            setTimeout(() => {
                setNotification(prev => ({...prev, show: false}));
            }, 3000);
        }
    };

    const closeNotification = () => {
        setNotification(prev => ({...prev, show: false}));
    };

    // API mutation for creating a client
    const createClientMutation = useMutation({
        mutationFn: async (formData) => {
            const accountId = await secureStorage.getItem("selectedAccount");

            return api.post('/clients', formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'multipart/form-data'
                }
            });
        },
        onSuccess: async (response) => {
            // Loyalty program on → the new client gets a card right away.
            // Best-effort: the card can still be created from the client form.
            let cardIssued = false;
            const createdId = response?.data?.id;
            if (createdId) {
                try {
                    const program = await fetchLoyaltyProgram();
                    if (program?.mode && program.mode !== 'off') {
                        await createLoyaltyCard(createdId);
                        cardIssued = true;
                    }
                } catch (e) {
                    console.warn('Loyalty card not issued for new client:', e);
                }
            }
            showNotification(
                'success',
                cardIssued ? 'Client ajouté avec sa carte de fidélité !' : 'Client ajouté avec succès!'
            );
            reset();
            setClientImage(null);
            setTimeout(() => {
                navigate('/clients');
            }, 2000);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                'Une erreur est survenue lors de la création du client';
            showNotification('error', errorMessage);
        }
    });

    const onSubmit = (data) => {
        // Create FormData object for submitting to the API
        const formData = new FormData();

        // Add all form data to FormData object
        formData.append("first_name", data.firstName);
        formData.append("last_name", data.lastName);
        formData.append("birthday", data.birthday);
        formData.append("adresse", data.address);
        formData.append("city", data.city);
        formData.append("country_code", data.countryCode);
        formData.append("phone", data.phone);
        formData.append("email", data.email);
        formData.append("device_name_created", "web");
        formData.append("device_name_updated", "web");
        formData.append("active", "true");
        if (data.civilite) formData.append("civilite", data.civilite);
        if (data.clientTypeId) formData.append("client_type_id", data.clientTypeId);

        // Add image if selected
        if (clientImage && clientImage.startsWith('data:')) {
            // Convert base64 to file object
            fetch(clientImage)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], "client_image.png", {type: "image/png"});
                    formData.append("image", file);

                    // Submit the form data
                    createClientMutation.mutate(formData);
                });
        } else {
            // Submit without image
            createClientMutation.mutate(formData);
        }
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            <FormHeader
                title="Nouveau client"
                backTo="/clients"
                onSave={() => formRef.current.requestSubmit()}
                saving={createClientMutation.isLoading}
            />

            <form ref={formRef} onSubmit={handleSubmit(onSubmit)}
                  className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-6 sm:space-y-8 w-full">
                <ClientPhoto
                    image={clientImage}
                    initials={initialsOf(watch("firstName"), watch("lastName"))}
                    onPick={triggerImageUpload}
                    onRemove={removeImage}
                />
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden"/>

                <div className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <Field id="firstName" label="Prénom" required error={errors.firstName}>
                            <input id="firstName" type="text" autoComplete="off" placeholder="Prénom"
                                   className={inputClass(errors.firstName)}
                                   {...register('firstName', {required: "Le prénom est obligatoire"})}/>
                        </Field>
                        <Field id="lastName" label="Nom" required error={errors.lastName}>
                            <input id="lastName" type="text" autoComplete="off" placeholder="Nom"
                                   className={inputClass(errors.lastName)}
                                   {...register('lastName', {required: "Le nom est obligatoire"})}/>
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <FormListbox
                            label="Civilité"
                            options={CIVILITES}
                            value={watch("civilite")}
                            onChange={(id) => setValue("civilite", id)}
                            placeholder="Sélectionner"
                            required={false}
                        />
                        <FormListbox
                            label="Type de client"
                            options={clientTypes}
                            value={watch("clientTypeId")}
                            onChange={(id) => setValue("clientTypeId", id)}
                            placeholder="Standard"
                            required={false}
                            labelAction={
                                <button
                                    type="button"
                                    onClick={() => setShowClientTypeEditor(true)}
                                    className="text-xs font-medium text-primary hover:text-primary-dark"
                                >
                                    + Nouveau type
                                </button>
                            }
                        />
                    </div>

                    <Section>
                        <Field id="email" label="Email" required error={errors.email}>
                            <input id="email" type="email" autoComplete="off" placeholder="client@example.com"
                                   className={inputClass(errors.email)}
                                   {...register('email', {
                                       required: "L'email est obligatoire",
                                       pattern: {
                                           value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                           message: "Adresse email invalide"
                                       }
                                   })}/>
                        </Field>

                        <Field id="phone" label="Téléphone" required error={errors.phone}>
                            <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
                                <div className="w-full sm:w-36">
                                    <FormListbox
                                        options={COUNTRY_CODES}
                                        value={watch("countryCode") || "212"}
                                        onChange={(id) => setValue("countryCode", id)}
                                        error={errors.countryCode?.message}
                                        placeholder="Code pays"
                                        required={false}
                                        displayValue={(item) => `${item.flag} ${item.name}`}
                                    />
                                </div>
                                <input id="phone" type="text" autoComplete="off" placeholder="Numéro de téléphone"
                                       className={`flex-1 min-w-0 ${inputClass(errors.phone)}`}
                                       {...register('phone', {
                                           required: "Le numéro est obligatoire",
                                           pattern: {
                                               value: /^[0-9-]{3,15}$/,
                                               message: "Format de téléphone invalide"
                                           }
                                       })}/>
                            </div>
                        </Field>
                    </Section>

                    <Section>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <Field id="address" label="Adresse" required error={errors.address}>
                                <input id="address" type="text" autoComplete="off" placeholder="Adresse"
                                       className={inputClass(errors.address)}
                                       {...register('address', {required: "L'adresse est obligatoire"})}/>
                            </Field>
                            <Field id="city" label="Ville" required error={errors.city}>
                                <input id="city" type="text" autoComplete="off" placeholder="Ville"
                                       className={inputClass(errors.city)}
                                       {...register('city', {required: "La ville est obligatoire"})}/>
                            </Field>
                        </div>

                        <Field id="birthday" label="Date de naissance" required error={errors.birthday}>
                            <Controller name="birthday" control={control}
                                        rules={{required: "La date de naissance est obligatoire"}}
                                        render={({field}) => (
                                            <DateInput id="birthday" required value={field.value || ''} name={field.name}
                                                       max={new Date().toISOString().slice(0, 10)}
                                                       onChange={(e) => field.onChange(e.target.value)}
                                                       className={inputClass(errors.birthday)}/>
                                        )}/>
                        </Field>
                    </Section>
                </div>
            </form>

            {/* Quick create client type */}
            {showClientTypeEditor && (
                <ClientTypeEditor
                    clientType={null}
                    onClose={() => setShowClientTypeEditor(false)}
                    showNotification={showNotification}
                    onSaved={(created) => setValue("clientTypeId", created.id)}
                />
            )}

            {/* Notification */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={closeNotification}
            />

            {/* Loading overlay */}
            {createClientMutation.isLoading && (
                <div className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 flex items-center justify-center backdrop-blur-sm">
                    <div className="mx-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg flex items-center transition-colors duration-200">
                        <Loader className="w-5 h-5 text-primary dark:text-primary-400 animate-spin mr-3"/>
                        <span className="text-gray-900 dark:text-gray-100">Création du client en cours...</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientNewForm;
